import { useEffect, useState } from 'react';
import { Settings } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/lib/hooks/use-toast';
import { importTaxConfigService } from '../services/import-tax-config-service';

interface ImportTaxConfigDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ImportTaxConfigDialog({ open, onOpenChange }: ImportTaxConfigDialogProps) {
  const { toast } = useToast();
  const [taxPct, setTaxPct] = useState('13');
  const [internacionPct, setInternacionPct] = useState('0');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    importTaxConfigService
      .get()
      .then((cfg) => {
        setTaxPct(String((cfg.importTaxRate ?? 0.13) * 100));
        setInternacionPct(String((cfg.importInternacionRate ?? 0) * 100));
      })
      .catch(() => {
        toast({ title: 'Error cargando tasas', variant: 'destructive' });
      })
      .finally(() => setLoading(false));
  }, [open, toast]);

  const parsePct = (val: string): number | null => {
    const n = parseFloat(val.replace(',', '.').trim());
    if (isNaN(n) || n < 0 || n > 100) return null;
    return n / 100;
  };

  const handleSave = async () => {
    const taxRate = parsePct(taxPct);
    const internacionRate = parsePct(internacionPct);
    if (taxRate === null) {
      toast({ title: 'Tasa IVA inválida (0–100)', variant: 'destructive' });
      return;
    }
    if (internacionRate === null) {
      toast({ title: 'Tasa Internación inválida (0–100)', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      await importTaxConfigService.update({
        importTaxRate: taxRate,
        importInternacionRate: internacionRate,
      });
      toast({ title: 'Tasas actualizadas' });
      onOpenChange(false);
    } catch (err: any) {
      const status = err?.response?.status;
      const body = err?.response?.data?.error ?? 'Error desconocido';
      toast({
        title: `Error al guardar${status ? ` (${status})` : ''}`,
        description: body,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="w-4 h-4" />
            Configurar tasas de Import/Export
          </DialogTitle>
          <DialogDescription>
            Estas tasas se aplican al cálculo automático de costos nacionalizados (FOB + Flete +
            Seguro + Aranceles + IVA + Internación) de todas las órdenes de tu empresa.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1">
            <label className="text-sm font-medium">Tasa IVA (%)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              max="100"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50"
              value={taxPct}
              onChange={(e) => setTaxPct(e.target.value)}
              disabled={loading || saving}
            />
            <p className="text-xs text-muted-foreground">
              Bolivia 13%, Argentina 21%, Perú 18%, Chile 19%.
            </p>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium">Tasa Internación (%)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              max="100"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50"
              value={internacionPct}
              onChange={(e) => setInternacionPct(e.target.value)}
              disabled={loading || saving}
            />
            <p className="text-xs text-muted-foreground">
              Almacenaje, despacho aduanero, etc. Aplica sobre CIF.
            </p>
          </div>

          <div className="rounded-md bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
            <p className="font-semibold mb-1">Otras tasas (no editables):</p>
            <ul className="space-y-0.5">
              <li>• Seguro: 1,5 % × (FOB + Flete)</li>
              <li>• Aranceles: el GA de la partida arancelaria de cada producto</li>
              <li>• IVA: (CIF + Aranceles) × 13/87 = 14,943 % — Ley N°843 Art. 8</li>
            </ul>
          </div>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" disabled={saving}>
              Cancelar
            </Button>
          </DialogClose>
          <Button onClick={handleSave} disabled={loading || saving}>
            {saving ? 'Guardando…' : 'Guardar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
